/**
 * Compact Todo list widget shown on the admin dashboard. Fetches from the
 * existing `/todos` endpoint used by the full TodoList page, filters to open
 * items, and renders the top 5 with a quick-complete action + a link to the
 * full page.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/auth';
import { useTranslation } from '../../lib/i18n';
import { CheckSquare, ArrowRight, Loader2 } from 'lucide-react';

const STATUS_LABEL = {
  pending: 'Açık',
  in_progress: 'Devam',
  done: 'Tamamlandı',
};

export default function DashboardTodoWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);

  const fetchTodos = async () => {
    try {
      const res = await api.get('/todos');
      if (res.data?.success) {
        // Open items first, then IN_PROGRESS; skip DONE; cap at 5.
        const all = res.data.data || [];
        const active = all
          .filter((x) => x.status !== 'done')
          .sort((a, b) => {
            const order = { pending: 0, in_progress: 1 };
            return (order[a.status] ?? 9) - (order[b.status] ?? 9);
          })
          .slice(0, 5);
        setTodos(active);
      }
    } catch (e) {
      // silent — widget is non-critical on dashboard
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTodos(); }, []);

  const markDone = async (id) => {
    setUpdatingId(id);
    try {
      await api.put(`/todos/${id}`, { status: 'done' });
      // Optimistic: remove from local list.
      setTodos((prev) => prev.filter((x) => x.id !== id));
    } catch {
      // ignore; user can retry
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div
      className="bg-white dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700/50 p-5"
      data-testid="dashboard-todo-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="h-4 w-4 text-indigo-500" />
          <h3
            className="text-sm font-semibold text-slate-700 dark:text-slate-200"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('dashboard.todos') || 'Yapılacaklar'}
          </h3>
          {todos.length > 0 && (
            <span className="text-xs text-slate-400">({todos.length})</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/dashboard/todo')}
          className="text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 inline-flex items-center gap-1"
          data-testid="dashboard-todo-open-full"
        >
          {t('dashboard.viewAll') || 'Tümü'}
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : todos.length === 0 ? (
        <p className="text-xs text-slate-400 py-4 text-center">
          {t('dashboard.noTodos') || 'Açık görev yok.'}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {todos.map((todo) => (
            <li
              key={todo.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700/40 group"
              data-testid={`dashboard-todo-item-${todo.id}`}
            >
              <button
                type="button"
                onClick={() => markDone(todo.id)}
                disabled={updatingId === todo.id}
                className="h-4 w-4 rounded border-2 border-slate-300 dark:border-slate-600 hover:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50 shrink-0 transition-colors"
                title={t('dashboard.markDone') || 'Tamamla'}
                data-testid={`dashboard-todo-done-${todo.id}`}
              />
              <button
                type="button"
                onClick={() => navigate('/dashboard/todo')}
                className="text-left text-sm text-slate-700 dark:text-slate-300 flex-1 min-w-0 truncate hover:text-indigo-600 dark:hover:text-indigo-400"
              >
                {todo.title}
              </button>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${
                  todo.status === 'in_progress'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300'
                }`}
              >
                {STATUS_LABEL[todo.status] || todo.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
